import React from "react";
import { TERM_SHEET_INPUT_TYPES } from "components/TermSheets";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import { colors } from "style";
import { CURRENCIES, ARCHETYPES } from "constants/index";

/**
 * Normalize a value for comparison
 * Handles type coercion, null/empty, and whitespace differences
 * @param {*} val - The value to normalize
 * @returns {string} - Normalized string value
 */
const normalizeValue = (val) => {
  if (val === null || val === undefined || val === "") return "";

  return String(val).trim();
};

/**
 * Check if a value has actually changed from its original
 * @param {*} originalValue - The original value from the assignment
 * @param {*} changedValue - The new value from the changes object
 * @returns {boolean} - True if values are different after normalization
 */
const hasValueChanged = (originalValue, changedValue) => normalizeValue(changedValue) !== normalizeValue(originalValue);

const calculateBuyingAmount = (row, changes) => {
  const rate = changes?.buyingBasePrice ?? row.buyingBasePrice;
  const hours = changes?.averageCapacity ?? row.averageCapacity;
  const commission = changes?.commissionPercentage ?? row.commissionPercentage;

  return rate * hours * (1 + (commission || 0) / 100);
};

const calculateConvertedBuyingAmount = (row, changes, conversionMatrix, toCurrency) => {
  const buyingAmount = calculateBuyingAmount(row, changes);
  const buyingCurrency = changes?.buyingCurrency ?? row.buyingCurrency;
  const conversionRate = conversionMatrix?.[buyingCurrency]?.[toCurrency] ?? 1;

  return buyingAmount * conversionRate;
};

const calculateSellingAmount = (row, changes) => {
  const sellingBasePrice = changes?.sellingBasePrice ?? row.sellingBasePrice;
  const sellingDiscount = changes?.sellingDiscount ?? row.sellingDiscount;

  return sellingBasePrice - sellingDiscount;
};

const calculateConvertedSellingAmount = (row, changes, { conversionMatrix, sellInCurrency }) => {
  const sellingAmount = calculateSellingAmount(row, changes);
  const sellingCurrency = changes?.sellingCurrency ?? row.sellingCurrency;
  const sellingRate = conversionMatrix?.[sellingCurrency]?.[sellInCurrency] ?? 1;

  return sellingAmount * sellingRate;
};

const calculateSellingMargin = (row, changes, context) => {
  const convertedSelling = calculateConvertedSellingAmount(row, changes, context);

  const { conversionMatrix, sellInCurrency } = context;

  // Get buying amount and convert to sellInCurrency
  const convertedBuying = calculateConvertedBuyingAmount(row, changes, conversionMatrix, sellInCurrency);

  return convertedSelling - convertedBuying;
};

const calculateSellingMarginPercentage = (row, changes, context) => {
  const marginAmount = calculateSellingMargin(row, changes, context);
  const convertedSelling = calculateConvertedSellingAmount(row, changes, context);

  if (convertedSelling === 0) return 0;

  return (marginAmount / convertedSelling) * 100;
};

// Field schema for submitable termsheet fields
// Maps field names to their type converters
const SUBMITABLE_FIELD_SCHEMA = {
  onboardedAt: (v) => v, // String (date)
  offboardedAt: (v) => v, // String (date)
  averageCapacity: (v) => parseInt(v, 10), // Int
  buyingBasePrice: parseFloat, // Float
  buyingCurrency: (v) => v, // String
  commissionPercentage: parseFloat, // Float
  sellingBasePrice: parseFloat, // Float
  sellingDiscount: parseFloat, // Float
  archetype: (v) => v, // String
};

const LEGACY_TERMSHEET_COLUMNS_CONFIG = [
  { key: "member", label: "Member", width: 350, sticky: true },
  {
    key: "archetype",
    label: "Assigned Archetype",
    width: 120,
    type: ({ termSheet }) =>
      isInactiveTermSheet(termSheet) ? TERM_SHEET_INPUT_TYPES.text : TERM_SHEET_INPUT_TYPES.select,
    valueFunc: (value, { termSheet }) =>
      isInactiveTermSheet(termSheet) ? Object.values(ARCHETYPES).find(({ id }) => id === value)?.name : value,
    options: Object.values(ARCHETYPES).map(({ name, id }) => ({ value: id, label: name })),
    border: true,
  },
  {
    key: "onboardedAt",
    label: "Onboarded Date",
    width: 115,
    type: TERM_SHEET_INPUT_TYPES.date,
    readOnlyFunc: ({ termSheet }) => isInactiveTermSheet(termSheet),
  },
  {
    key: "offboardedAt",
    label: "Offboarded Date",
    width: 115,
    type: TERM_SHEET_INPUT_TYPES.date,
    readOnlyFunc: ({ termSheet }) => isInactiveTermSheet(termSheet),
  },
  {
    key: "averageCapacity",
    label: "Buying Quantity",
    width: 90,
    type: TERM_SHEET_INPUT_TYPES.hours,
    readOnlyFunc: ({ termSheet }) => isInactiveTermSheet(termSheet),
  },
  {
    key: "buyingBasePrice",
    label: "Buying Rate",
    width: 150,
    type: ({ termSheet }) =>
      isInactiveTermSheet(termSheet) ? TERM_SHEET_INPUT_TYPES.currency : TERM_SHEET_INPUT_TYPES.currencyWithSelector,
    readOnlyFunc: ({ termSheet }) => isInactiveTermSheet(termSheet),
    currencyField: ({ buyingCurrency, getCellValue }) => getCellValue?.("buyingCurrency") || buyingCurrency,
    currencyOptions: CURRENCIES,
    currencyKey: "buyingCurrency",
  },
  {
    key: "commissionPercentage",
    label: "Commission%",
    width: 100,
    type: TERM_SHEET_INPUT_TYPES.percent,
    readOnlyFunc: ({ termSheet }) => isInactiveTermSheet(termSheet),
  },
  {
    key: "buyingAmount",
    label: "Buying Amount",
    width: 115,
    type: TERM_SHEET_INPUT_TYPES.currency,
    readOnly: true,
    derivedField: true,
    currencyField: ({ buyingCurrency, getCellValue }) => getCellValue?.("buyingCurrency") || buyingCurrency,
    derivedFrom: { formula: calculateBuyingAmount },
  },
  {
    key: "convertedBuyingAmount",
    label: ({ buyInCurrency }) => `Buying Amount (${buyInCurrency})`,
    width: 115,
    type: TERM_SHEET_INPUT_TYPES.currency,
    readOnly: true,
    derivedField: true,
    highlightColor: colors.purpleRainLight3,
    derivedFrom: {
      formula: (row, changes, { conversionMatrix, buyInCurrency } = {}) =>
        calculateConvertedBuyingAmount(row, changes, conversionMatrix, buyInCurrency),
    },
    currencyField: ({ buyInCurrency }) => buyInCurrency,
  },
  {
    key: "sellingBasePrice",
    label: "Selling Base Price",
    width: 115,
    type: TERM_SHEET_INPUT_TYPES.currency,
    readOnlyFunc: ({ termSheet }) => isInactiveTermSheet(termSheet) || isActiveTermSheet(termSheet),
    currencyField: ({ sellingCurrency, getCellValue, billingCurrency }) =>
      getCellValue?.("sellingCurrency") || sellingCurrency || billingCurrency,
  },
  {
    key: "sellingDiscount",
    label: "Discount",
    width: 100,
    type: TERM_SHEET_INPUT_TYPES.currency,
    readOnlyFunc: ({ termSheet }) => isInactiveTermSheet(termSheet) || isActiveTermSheet(termSheet),
    currencyField: ({ sellingCurrency, getCellValue, billingCurrency }) =>
      getCellValue?.("sellingCurrency") || sellingCurrency || billingCurrency,
  },
  {
    key: "convertedSellingAmount",
    label: ({ sellInCurrency }) => `Selling Amount (${sellInCurrency})`,
    width: 115,
    type: TERM_SHEET_INPUT_TYPES.currency,
    readOnly: true,
    derivedField: true,
    highlightColor: colors.purpleRainLight3,
    derivedFrom: { formula: calculateConvertedSellingAmount },
    currencyField: ({ sellInCurrency }) => sellInCurrency,
  },
  {
    key: "marginAmount",
    label: ({ sellInCurrency }) => `Margin Amount (${sellInCurrency})`,
    width: 115,
    type: TERM_SHEET_INPUT_TYPES.currency,
    readOnly: true,
    derivedField: true,
    derivedFrom: { formula: calculateSellingMargin },
    currencyField: ({ sellInCurrency }) => sellInCurrency,
  },
  {
    key: "marginPercentage",
    label: "Margin%",
    width: 90,
    type: TERM_SHEET_INPUT_TYPES.percent,
    readOnly: true,
    derivedField: true,
    derivedFrom: { formula: calculateSellingMarginPercentage },
  },
];

const TERM_SHEET_STATES = {
  draft: "DRAFT",
  active: "ACTIVE",
  inactive: "INACTIVE",
};

const TERM_SHEET_STATUSES = {
  DRAFT: "Draft",
  ACTIVE: "Active",
  INACTIVE: "Inactive",
};

const TERMSHEET_STATUS_COLORS = {
  [TERM_SHEET_STATUSES.ACTIVE]: { color: colors.darkGreen, background: colors.lightGreen },
  [TERM_SHEET_STATUSES.DRAFT]: { color: colors.black, background: colors.darkYellow },
  [TERM_SHEET_STATUSES.INACTIVE]: { color: colors.black, background: colors.grayAnatomyLight2 },
};

const getStateText = ({ state, versionNumber, createdAt }) => {
  const stateText = TERM_SHEET_STATUSES[state];

  switch (stateText) {
    case TERM_SHEET_STATUSES.DRAFT:
      return <FormattedMessage id="Squads.Squad.TermSheet.Table.Header.Scenario" values={{ versionNumber }} />;
    case TERM_SHEET_STATUSES.ACTIVE:
      return (
        <FormattedMessage
          id="Squads.Squad.TermSheet.Table.Header.StateWithTimeStamp"
          values={{ state: TERM_SHEET_STATUSES[state], timestamp: moment(createdAt).fromNow() }}
        />
      );
    default:
      return stateText;
  }
};

const isActiveTermSheet = (termSheet) => TERM_SHEET_STATUSES[termSheet?.state] === TERM_SHEET_STATUSES.ACTIVE;
const isDraftTermSheet = (termSheet) => TERM_SHEET_STATUSES[termSheet?.state] === TERM_SHEET_STATUSES.DRAFT;
const isInactiveTermSheet = (termSheet) => TERM_SHEET_STATUSES[termSheet?.state] === TERM_SHEET_STATUSES.INACTIVE;

// Columns that should display sum totals in the totals row
const TOTALS_SUM_COLUMNS = [
  "averageCapacity",
  "convertedBuyingAmount",
  "sellingBasePrice",
  "sellingDiscount",
  "convertedSellingAmount",
  "marginAmount",
];

/**
 * Determine if a column should show totals
 * @param {String} columnKey - The column key to check
 * @returns {Boolean} - True if the column should show totals
 */
const shouldShowTotals = (columnKey) =>
  columnKey === "member" || columnKey === "marginPercentage" || TOTALS_SUM_COLUMNS.includes(columnKey);

/**
 * Get the display configuration for a totals cell
 * @param {Object} column - Column configuration object
 * @param {Object} totals - Calculated totals object
 * @param {Object} context - Context with termSheetStatus, buyInCurrency, sellInCurrency
 * @returns {Object|null} - Display config object or null if column shouldn't show totals
 */
const getTotalsDisplayConfig = (column, totals, context = {}) => {
  const { key, type, currencyField } = column;
  const { termSheetStatus, ...rest } = context;

  if (!totals) return null;

  // Member column shows "Total" label
  if (key === "member") {
    return {
      component: FormattedMessage,
      props: {
        id: "Global.Total",
      },
    };
  }

  // Margin percentage shows average
  if (key === "marginPercentage") {
    return {
      type: "percent",
      value: totals.marginPercentage,
      readOnly: true,
    };
  }

  // Sum columns show totals with appropriate formatting
  if (TOTALS_SUM_COLUMNS.includes(key)) {
    // Determine the input type
    let inputType = type;
    if (typeof type === "function") {
      inputType = type({ termSheetStatus });
    }

    // Get currency for currency fields
    let currency;
    if (typeof currencyField === "function") {
      currency = currencyField(rest);
    }

    return {
      type: inputType,
      value: totals[key],
      currency,
      readOnly: true,
    };
  }

  return null;
};

/**
 * Calculate totals for all assignments in a termsheet
 * @param {Array} assignments - Array of assignment objects
 * @param {Object} changes - Object mapping assignment IDs to their changes
 * @param {Object} context - Context object containing conversionMatrix, buyInCurrency, sellInCurrency
 * @returns {Object|null} - Object containing sum totals and average margin percentage, or null if no assignments
 */
const calculateTotals = (assignments, changes = {}, { conversionMatrix, buyInCurrency, sellInCurrency } = {}) => {
  if (!assignments || assignments.length === 0) {
    return null;
  }

  const sums = {
    averageCapacity: 0,
    convertedBuyingAmount: 0,
    sellingBasePrice: 0,
    sellingDiscount: 0,
    convertedSellingAmount: 0,
    marginAmount: 0,
  };

  assignments.forEach((assignment) => {
    const assignmentChanges = changes[assignment.id] || {};

    // Get values considering pending changes - convert to numbers to avoid string concatenation
    sums.averageCapacity += Number(assignmentChanges.averageCapacity ?? assignment.averageCapacity) || 0;
    sums.convertedBuyingAmount +=
      calculateConvertedBuyingAmount(assignment, assignmentChanges, conversionMatrix, buyInCurrency) || 0;
    sums.sellingBasePrice += Number(assignmentChanges.sellingBasePrice ?? assignment.sellingBasePrice) || 0;
    sums.sellingDiscount += Number(assignmentChanges.sellingDiscount ?? assignment.sellingDiscount) || 0;
    sums.convertedSellingAmount +=
      calculateConvertedSellingAmount(assignment, assignmentChanges, { conversionMatrix, sellInCurrency }) || 0;
    sums.marginAmount +=
      calculateSellingMargin(assignment, assignmentChanges, { conversionMatrix, sellInCurrency, buyInCurrency }) || 0;
  });

  // Calculate average margin percentage
  const marginPercentage =
    sums.convertedSellingAmount > 0 ? (sums.marginAmount / sums.convertedSellingAmount) * 100 : 0;

  return {
    ...sums,
    marginPercentage,
  };
};

const getTransformedParams = (changes, assignments = []) => {
  const transformed = [];

  Object.entries(changes).forEach(([assignmentId, assignmentChanges]) => {
    // Find the original assignment to get unchanged field values
    const originalAssignment = assignments.find(({ id }) => id == assignmentId);

    const filteredChanges = Object.keys(assignmentChanges).reduce((acc, key) => {
      if (key in SUBMITABLE_FIELD_SCHEMA) {
        const converter = SUBMITABLE_FIELD_SCHEMA[key];
        acc[key] = converter(assignmentChanges[key]);
      }

      return acc;
    }, {});

    // Always include onboardedAt and offboardedAt from original if not in changes
    let params = { ...filteredChanges, id: assignmentId };

    if (!params.onboardedAt) {
      params = { ...params, onboardedAt: originalAssignment?.onboardedAt };
    }

    if (!params.offboardedAt) {
      params = { ...params, offboardedAt: originalAssignment?.offboardedAt };
    }

    transformed.push(params);
  });

  return transformed;
};

export {
  TERMSHEET_STATUS_COLORS,
  TERM_SHEET_STATUSES,
  TERM_SHEET_STATES,
  getStateText,
  isActiveTermSheet,
  isDraftTermSheet,
  isInactiveTermSheet,
  calculateTotals,
  TOTALS_SUM_COLUMNS,
  shouldShowTotals,
  getTotalsDisplayConfig,
  getTransformedParams,
  normalizeValue,
  hasValueChanged,
};

export default LEGACY_TERMSHEET_COLUMNS_CONFIG;
